import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, CheckCircle2, Eye } from "lucide-react";

const RowList = ({ items, tone, render }) => (
  <ul className={`text-[11px] space-y-0.5 ${tone}`}>
    {items.slice(0, 50).map((it, i) => render(it, i))}
  </ul>
);

export function ImportPreviewDialog({ entity, preview, committing, onCancel, onConfirm }) {
  const skipped = preview?.skipped || [];
  const errors = preview?.errors || [];
  const willWrite = (preview?.created || 0) + (preview?.updated || 0);
  return (
    <Dialog open={!!preview} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-lg" data-testid={`import-preview-${entity}`}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Eye className="h-5 w-5 text-blue-600" /> Import Preview
          </DialogTitle>
          <DialogDescription>Dry run — nothing has been saved yet. Review, then confirm to write.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-4 gap-2 text-center mb-1">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 py-2"><div className="text-lg font-bold text-emerald-600">{preview?.created || 0}</div><div className="text-[10px] uppercase text-slate-500">Create</div></div>
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 py-2"><div className="text-lg font-bold text-indigo-600">{preview?.updated || 0}</div><div className="text-[10px] uppercase text-slate-500">Update</div></div>
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 py-2"><div className="text-lg font-bold text-amber-600">{skipped.length}</div><div className="text-[10px] uppercase text-slate-500">Skip</div></div>
          <div className="rounded-lg border border-red-200 bg-red-50/50 py-2"><div className="text-lg font-bold text-red-600">{errors.length}</div><div className="text-[10px] uppercase text-slate-500">Errors</div></div>
        </div>
        <div className="space-y-3 max-h-[40vh] overflow-y-auto">
          {skipped.length > 0 && (
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold text-slate-600 mb-1.5">Will skip ({skipped.length})</p>
              <RowList items={skipped} tone="text-slate-500" render={(s, i) => (
                <li key={`skip-${s.row}-${i}`}>Row {s.row}: <b className="text-slate-700">{s.key}</b> — {s.reason}</li>
              )} />
            </div>
          )}
          {errors.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50/50 p-3">
              <p className="text-xs font-semibold text-red-600 mb-1.5">Errors ({errors.length}) — these rows won't import</p>
              <RowList items={errors} tone="text-red-700" render={(er, i) => (
                <li key={`err-${er.row}-${i}`}>Row {er.row}: {er.reason}</li>
              )} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={committing} onClick={onCancel} data-testid={`import-preview-cancel-${entity}`}>Cancel</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={committing || willWrite === 0} onClick={onConfirm} data-testid={`import-confirm-${entity}`}>
            {committing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Confirm import ({willWrite})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
